import React, { useState, useMemo } from "react";
import { ArrowRight, ArrowLeft, Sparkles, Download, X as XIcon, Loader2, Heart, Briefcase, Home, GraduationCap, Shield, TrendingUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtINR = (n) => {
  if (n == null || isNaN(n)) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

// Quick-pick chips
const INCOME_CHIPS = [25000, 50000, 100000, 150000, 250000, 500000];
const AGE_CHIPS = [25, 30, 35, 40, 45, 50];
const KID_AGE_CHIPS = [2, 5, 8, 12, 15];

const STEPS = [
  { key: "intro", label: "Start" },
  { key: "age", label: "Age" },
  { key: "income", label: "Income" },
  { key: "city", label: "City" },
  { key: "family", label: "Family" },
  { key: "kids", label: "Kids" },
  { key: "coverage", label: "Existing" },
  { key: "loans", label: "Loans" },
  { key: "retire", label: "Retirement" },
  { key: "result", label: "Result" },
];

const cityChips = ["Metro", "Tier-1", "Tier-2", "Tier-3"];
const maritalChips = ["Single", "Married", "Married with kids"];
const educationGoals = ["Engineering (India)", "Engineering (Abroad)", "Medical (India)", "Medical (Abroad)", "Liberal Arts / Other"];

/**
 * Gamified Need Analysis wizard — 1 question per screen.
 *
 * Props:
 *   open: boolean
 *   onClose: ()
 *   clientId?: string   — saves analysis against this client
 *   leadId?: string     — saves analysis against this lead
 *   onSaved?: (doc) => void  — invoked with the persisted doc
 *   initial?: partial inputs (e.g., age, marital_status from client profile)
 */
export default function NeedAnalysisWizard({ open, onClose, clientId, leadId, initial, onSaved }) {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState(() => ({
    age: 30,
    monthly_take_home: 75000,
    city_tier: "Tier-1",
    marital_status: "Single",
    num_kids: 0,
    kid_ages: [],
    has_parents_dependent: false,
    existing_life_cover: 0,
    existing_health_cover: 500000,
    existing_investments: 0,
    outstanding_loans: 0,
    retirement_age: 60,
    education_goal: "Engineering (India)",
    ...(initial || {}),
  }));
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));
  const stepKey = STEPS[step].key;
  const progress = ((step) / (STEPS.length - 1)) * 100;

  // Skip 'kids' if user said Single / Married without kids
  const shouldSkip = (s) => {
    if (s.key === "kids" && inputs.marital_status !== "Married with kids") return true;
    return false;
  };
  const next = async () => {
    let s = step + 1;
    while (s < STEPS.length && shouldSkip(STEPS[s])) s++;
    if (STEPS[s]?.key === "result") {
      await runCompute();
    }
    setStep(s);
  };
  const prev = () => {
    let s = step - 1;
    while (s > 0 && shouldSkip(STEPS[s])) s--;
    setStep(Math.max(0, s));
  };

  const runCompute = async () => {
    setComputing(true);
    try {
      const { data } = await api.post("/need-analysis/compute", { inputs });
      setResult(data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not compute");
    } finally { setComputing(false); }
  };

  const save = async () => {
    if (!clientId && !leadId) { toast.error("Need a client or lead to save against"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/need-analysis", {
        client_id: clientId || null, lead_id: leadId || null, inputs,
      });
      setSaved(data);
      toast.success("Need analysis saved");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (!saved) { toast.error("Save the analysis first"); return; }
    try {
      const res = await api.get(`/need-analysis/${saved.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `NeedAnalysis-${saved.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 grid place-items-center p-4" data-testid="na-wizard">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Progress + close */}
        <div className="px-6 pt-6 pb-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-emerald-700 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-400">
              <span data-testid="na-step-label">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
              <span>{Math.round(progress)}%</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid="na-close"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="px-7 pb-7 min-h-[400px]">
          {/* STEP CONTENT */}
          {stepKey === "intro" && (
            <div className="text-center py-8" data-testid="na-step-intro">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-2xl grid place-items-center mb-4">
                <Sparkles className="w-8 h-8 text-emerald-700" />
              </div>
              <h2 className="font-display text-3xl font-black tracking-tight">Need Analysis</h2>
              <p className="text-stone-500 mt-2 max-w-sm mx-auto">A quick, gamified check of life cover, health cover, retirement, and your kids&apos; education in under 2 minutes.</p>
              <Button onClick={next} className="mt-6 rounded-full px-7 h-12 bg-emerald-900 hover:bg-emerald-950 text-white font-bold" data-testid="na-start">
                Let&apos;s go <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {stepKey === "age" && (
            <Step icon={Heart} title="How old are you today?" sub="Used to plan retirement and education timelines.">
              <Input type="number" value={inputs.age} onChange={(e) => set("age", parseInt(e.target.value || "0"))}
                className="h-16 text-3xl font-black text-center font-mono rounded-2xl border-2 border-emerald-200 focus:border-emerald-700"
                data-testid="na-age-input" />
              <ChipRow chips={AGE_CHIPS} value={inputs.age} onPick={(v) => set("age", v)} testIdPrefix="na-age-chip" />
            </Step>
          )}

          {stepKey === "income" && (
            <Step icon={Briefcase} title="Monthly take-home income (₹)" sub="Net amount after taxes & EMIs.">
              <Input type="number" value={inputs.monthly_take_home} onChange={(e) => set("monthly_take_home", parseFloat(e.target.value || "0"))}
                className="h-16 text-3xl font-black text-center font-mono rounded-2xl border-2 border-emerald-200 focus:border-emerald-700"
                data-testid="na-income-input" />
              <ChipRow chips={INCOME_CHIPS} value={inputs.monthly_take_home} onPick={(v) => set("monthly_take_home", v)} render={fmtINR} testIdPrefix="na-income-chip" />
            </Step>
          )}

          {stepKey === "city" && (
            <Step icon={Home} title="Which city tier do you live in?" sub="Health-cover recommendations vary across tiers.">
              <div className="grid grid-cols-2 gap-3 mt-2">
                {cityChips.map((c) => (
                  <button key={c} onClick={() => set("city_tier", c)} data-testid={`na-city-${c}`}
                    className={`px-4 py-4 rounded-xl border-2 font-bold transition ${inputs.city_tier === c ? "border-emerald-700 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-emerald-400"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </Step>
          )}

          {stepKey === "family" && (
            <Step icon={Heart} title="What's your family setup?" sub="Drives life-cover multiplier and dependent count.">
              <div className="grid grid-cols-1 gap-2.5 mt-2">
                {maritalChips.map((m) => (
                  <button key={m} onClick={() => set("marital_status", m)} data-testid={`na-marital-${m.replace(/\s+/g, '_')}`}
                    className={`px-4 py-4 rounded-xl border-2 font-bold transition text-left ${inputs.marital_status === m ? "border-emerald-700 bg-emerald-50 text-emerald-900" : "border-stone-200 text-stone-600 hover:border-emerald-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
              <label className="mt-4 flex items-center gap-3 cursor-pointer text-sm">
                <input type="checkbox" checked={inputs.has_parents_dependent} onChange={(e) => set("has_parents_dependent", e.target.checked)} data-testid="na-parents-dep" />
                <span>I support my parents financially</span>
              </label>
            </Step>
          )}

          {stepKey === "kids" && (
            <Step icon={GraduationCap} title="Kids — number & ages" sub="So we can plan their college corpus.">
              <div className="flex items-center gap-3">
                <Label className="font-semibold">How many kids?</Label>
                <Input type="number" min={0} max={6} value={inputs.num_kids}
                  onChange={(e) => {
                    const n = Math.max(0, Math.min(6, parseInt(e.target.value || "0")));
                    set("num_kids", n);
                    set("kid_ages", Array.from({length: n}, (_, i) => inputs.kid_ages[i] || 5));
                  }}
                  className="h-12 w-20 text-center font-mono font-bold text-xl" data-testid="na-num-kids" />
              </div>
              {Array.from({length: inputs.num_kids}).map((_, i) => (
                <div key={i} className="mt-3 bg-stone-50 rounded-xl p-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-emerald-800">Kid #{i + 1} — Age</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {KID_AGE_CHIPS.map((v) => (
                      <button key={v} onClick={() => {
                        const arr = [...inputs.kid_ages];
                        arr[i] = v;
                        set("kid_ages", arr);
                      }} data-testid={`na-kid-${i}-age-${v}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold ${inputs.kid_ages[i] === v ? "bg-emerald-700 text-white" : "bg-white border border-stone-300 text-stone-600"}`}>{v}y</button>
                    ))}
                    <Input type="number" min={0} max={25} value={inputs.kid_ages[i] || 0}
                      onChange={(e) => {
                        const arr = [...inputs.kid_ages];
                        arr[i] = parseInt(e.target.value || "0");
                        set("kid_ages", arr);
                      }} className="w-16 h-9 text-center font-mono" />
                  </div>
                </div>
              ))}
              {inputs.num_kids > 0 && (
                <div className="mt-4">
                  <Label className="text-xs font-bold uppercase tracking-wider text-stone-500">Default education goal per kid</Label>
                  <select value={inputs.education_goal} onChange={(e) => set("education_goal", e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-lg border border-stone-300 px-3 bg-white" data-testid="na-edu-goal">
                    {educationGoals.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              )}
            </Step>
          )}

          {stepKey === "coverage" && (
            <Step icon={Shield} title="Existing coverage" sub="What you already have. Be honest — gaps are good to know.">
              <FieldNumber label="Life cover (sum assured ₹)" value={inputs.existing_life_cover}
                onChange={(v) => set("existing_life_cover", v)} testId="na-life-existing" />
              <FieldNumber label="Health cover (sum insured ₹)" value={inputs.existing_health_cover}
                onChange={(v) => set("existing_health_cover", v)} testId="na-health-existing" />
              <FieldNumber label="Investments / savings (₹)" value={inputs.existing_investments}
                onChange={(v) => set("existing_investments", v)} testId="na-investments-existing" />
            </Step>
          )}

          {stepKey === "loans" && (
            <Step icon={Briefcase} title="Outstanding loans (₹)" sub="Home, car, education, personal — total balance.">
              <Input type="number" value={inputs.outstanding_loans}
                onChange={(e) => set("outstanding_loans", parseFloat(e.target.value || "0"))}
                className="h-16 text-3xl font-black text-center font-mono rounded-2xl border-2 border-emerald-200 focus:border-emerald-700"
                data-testid="na-loans-input" />
              <ChipRow chips={[0, 500000, 2000000, 5000000, 10000000]} value={inputs.outstanding_loans} onPick={(v) => set("outstanding_loans", v)} render={fmtINR} testIdPrefix="na-loan-chip" />
            </Step>
          )}

          {stepKey === "retire" && (
            <Step icon={TrendingUp} title="At what age do you want to retire?" sub="60 is standard. Earlier = need bigger corpus.">
              <Input type="number" value={inputs.retirement_age}
                onChange={(e) => set("retirement_age", parseInt(e.target.value || "0"))}
                className="h-16 text-3xl font-black text-center font-mono rounded-2xl border-2 border-emerald-200 focus:border-emerald-700"
                data-testid="na-retire-age" />
              <ChipRow chips={[50, 55, 60, 65]} value={inputs.retirement_age} onPick={(v) => set("retirement_age", v)} testIdPrefix="na-retire-chip" />
            </Step>
          )}

          {stepKey === "result" && (
            <div data-testid="na-step-result">
              {computing ? (
                <div className="py-20 text-center text-stone-400" data-testid="na-computing">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto" />
                  <div className="mt-3 text-sm">Computing your needs…</div>
                </div>
              ) : result ? (
                <ResultView res={result} inputs={inputs} onSave={save} onDownload={downloadPdf} saving={saving} saved={!!saved} clientLeadAvailable={!!(clientId || leadId)} />
              ) : null}
            </div>
          )}
        </div>

        {/* Footer nav */}
        {stepKey !== "intro" && stepKey !== "result" && (
          <div className="border-t border-stone-200 px-6 py-3 flex items-center justify-between">
            <Button variant="ghost" onClick={prev} disabled={step <= 0} data-testid="na-prev"><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
            <Button onClick={next} className="rounded-full bg-emerald-900 hover:bg-emerald-950" data-testid="na-next">
              {STEPS[step + 1]?.key === "result" ? "See result" : "Next"} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, sub, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-emerald-100 grid place-items-center shrink-0">
          <Icon className="w-6 h-6 text-emerald-700" />
        </div>
        <div>
          <h3 className="font-display text-xl font-black leading-tight">{title}</h3>
          <p className="text-sm text-stone-500 mt-0.5">{sub}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function ChipRow({ chips, value, onPick, render = (v) => v, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {chips.map((v) => (
        <button key={v} onClick={() => onPick(v)} data-testid={`${testIdPrefix}-${v}`}
          className={`px-3 py-1.5 rounded-full text-xs font-bold ${value === v ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
          {render(v)}
        </button>
      ))}
    </div>
  );
}

function FieldNumber({ label, value, onChange, testId }) {
  return (
    <div className="mt-3">
      <Label className="text-xs font-bold uppercase tracking-wider text-stone-500">{label}</Label>
      <Input type="number" value={value} onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
        className="mt-1.5 h-11 rounded-lg font-mono" data-testid={testId} />
      {value > 0 && <div className="text-[11px] text-stone-400 mt-1">{fmtINR(value)}</div>}
    </div>
  );
}

function ResultView({ res, inputs, onSave, onDownload, saving, saved, clientLeadAvailable }) {
  const score = res.risk_score;
  const scoreColor = score >= 75 ? "text-emerald-700 bg-emerald-100" : score >= 50 ? "text-amber-700 bg-amber-100" : "text-red-700 bg-red-100";
  const scoreLabel = score >= 75 ? "Strong" : score >= 50 ? "Needs attention" : "Critical gaps";
  return (
    <div className="space-y-5" data-testid="na-result">
      <div className="text-center">
        <div className={`inline-flex items-center gap-2 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${scoreColor}`}>
          <Sparkles className="w-3.5 h-3.5" /> Protection score
        </div>
        <div className="font-display text-6xl font-black mt-2" data-testid="na-score">{score}<span className="text-stone-300 text-3xl">/100</span></div>
        <div className="text-sm text-stone-500 mt-1">{scoreLabel}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <MetricCard label="Life cover gap" value={res.life_cover_gap} positive={res.life_cover_gap === 0} testId="na-card-life" />
        <MetricCard label="Health cover gap" value={res.health_cover_gap} positive={res.health_cover_gap === 0} testId="na-card-health" />
        <MetricCard label="Retirement gap" value={res.retirement_corpus_gap} positive={res.retirement_corpus_gap === 0} testId="na-card-retire" />
        <MetricCard label="Education corpus" value={res.education_corpus_needed} positive={res.education_corpus_needed === 0} testId="na-card-edu" />
        <MetricCard label="Emergency fund" value={res.emergency_fund_needed} positive={false} testId="na-card-emergency" />
        <MetricCard label="Monthly SIP advised" value={res.monthly_sip_required} positive={false} testId="na-card-sip" highlight />
      </div>

      <div className="bg-stone-50 rounded-xl p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">What to do next</div>
        <ul className="space-y-1.5 text-sm" data-testid="na-bullets">
          {(res.summary_bullets || []).map((b, i) => (<li key={i}>{b}</li>))}
        </ul>
      </div>

      {clientLeadAvailable && (
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          {!saved ? (
            <Button onClick={onSave} disabled={saving} className="flex-1 h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold" data-testid="na-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Save analysis</>)}
            </Button>
          ) : (
            <Button onClick={onDownload} className="flex-1 h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold" data-testid="na-download">
              <Download className="w-4 h-4 mr-2" /> Download PDF report
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, positive, testId, highlight }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? "border-amber-300 bg-amber-50" : positive ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-center gap-1.5">
        {positive ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> : value > 0 ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> : null}
        <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
      </div>
      <div className={`font-display text-2xl font-black mt-1 ${highlight ? "text-amber-900" : positive ? "text-emerald-700" : "text-stone-800"}`}>{fmtINR(value)}</div>
    </div>
  );
}
